import { config } from '@/utils/config'
import { AuthRequestError } from './auth-errors'

export interface WaitlistSubmitResponse {
  success: boolean
  message: string
}

export interface WaitlistTokenData {
  email: string
  organization_hint: string | null
  source: string
}

export interface WaitlistConfirmResponse {
  success: boolean
  redirect_url: string
  user_id?: string
}

/** Server rejection of a waitlist request; `serverMessage` is empty when the body had none. */
export class WaitlistRequestError extends Error {
  readonly serverMessage: string

  constructor(serverMessage: string) {
    super(serverMessage || 'Failed to verify email')
    this.name = 'WaitlistRequestError'
    this.serverMessage = serverMessage
  }

  /** auth-service rejects free-mail domains with a "work email" message. */
  get isBlockedDomain(): boolean {
    return this.serverMessage.includes('work email')
  }
}

// Step 1: Send the verification link. The request step collects the work email only;
// auth-service collects the organization name at set-password (confirm). A caller that
// still has a name may pass it as the prefill hint.
export async function verifyWaitlistEmail(email: string, organizationName?: string): Promise<WaitlistSubmitResponse> {
  const body: Record<string, string> = { email }
  if (organizationName) body.organization_name = organizationName

  const response = await fetch(`${config.authServiceUrl}/waitlist/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  let result: Partial<WaitlistSubmitResponse> & { error?: string } = {}
  try {
    result = await response.json()
  } catch {
    // Non-JSON body: fall through to the status check.
  }

  if (!response.ok) {
    throw new WaitlistRequestError(result.error || '')
  }

  return { success: result.success ?? true, message: result.message ?? '' }
}

// Step 1b: Exchange the code from the verification email for the link token
// (PLAN-20260912 slice 55). auth-service decides validity, expiry and the attempt
// limit and answers every refusal alike; the page continues to set-password with
// the token it returns, as the emailed link would.
export async function verifyWaitlistCode(email: string, code: string): Promise<string> {
  const response = await fetch(`${config.authServiceUrl}/waitlist/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  })

  if (!response.ok) {
    throw await AuthRequestError.fromResponse(response)
  }

  const result: { token?: string } = await response.json()
  if (!result.token) {
    throw new Error('verify-code answered without a token')
  }
  return result.token
}

// Step 2: Validate token (read-only, does not consume)
export async function validateWaitlistToken(token: string): Promise<WaitlistTokenData> {
  const response = await fetch(`${config.authServiceUrl}/waitlist/token?token=${encodeURIComponent(token)}`)

  if (!response.ok) {
    throw await AuthRequestError.fromResponse(response)
  }

  return response.json()
}

// Step 3: Confirm email and create account (with password for email signups)
export async function confirmWaitlistEmail(token: string, organizationName: string, password: string): Promise<WaitlistConfirmResponse> {
  const response = await fetch(`${config.authServiceUrl}/waitlist/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, organization_name: organizationName, password }),
  })

  if (!response.ok) {
    throw await AuthRequestError.fromResponse(response)
  }

  return response.json()
}
